import { memo, useMemo } from '../../../lib/teact/teact';

import type { TeactNode } from '../../../lib/teact/teact';

import buildClassName from '../../../util/buildClassName';
import renderText from '../helpers/renderText';

import styles from './MarkdownTable.module.scss';

type OwnProps = {
  text: string;
};

type ColumnAlign = 'start' | 'center' | 'end';

interface ParsedTable {
  headers: string[];
  alignments: ColumnAlign[];
  rows: string[][];
}

function parseCells(line: string): string[] {
  // Strip leading/trailing pipe and split by pipe
  const trimmed = line.replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function parseAlignment(cell: string): ColumnAlign {
  const trimmed = cell.trim();
  const isLeft = trimmed.startsWith(':');
  const isRight = trimmed.endsWith(':');
  if (isLeft && isRight) return 'center';
  if (isRight) return 'end';
  return 'start';
}

function parseTable(text: string): ParsedTable | undefined {
  const lines = text.trim().split('\n').filter((l) => l.trim().length > 0);
  if (lines.length < 2) return undefined;

  const headers = parseCells(lines[0]);

  // Second line should be separator
  const separatorCells = parseCells(lines[1]);
  const isSeparator = separatorCells.every((c) => /^:?-+:?$/.test(c.trim()));
  if (!isSeparator) return undefined;

  const alignments = separatorCells.map(parseAlignment);

  const rows = lines.slice(2)
    .filter((line) => {
      // Filter out repeated separator rows (some LLMs insert them between data rows)
      const cells = parseCells(line);
      return !cells.every((c) => /^:?-+:?$/.test(c.trim()));
    })
    .map(parseCells);

  return { headers, alignments, rows };
}

// Render inline markdown + HTML tags in table cells
function renderCell(text: string): TeactNode[] {
  // Split on <br>, <br/>, <br /> and render as line breaks
  const parts = text.split(/<br\s*\/?>/gi);
  const result: TeactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i > 0) result.push(<br />);
    result.push(...renderText(parts[i], ['simple_markdown', 'emoji', 'links']));
  }
  return result;
}

const MarkdownTable = ({ text }: OwnProps) => {
  const table = useMemo(() => parseTable(text), [text]);

  if (!table) {
    // Fallback: render as preformatted text
    return <pre className={styles.root}>{text}</pre>;
  }

  const { headers, alignments, rows } = table;

  return (
    <div className={styles.root}>
      <table className={styles.table}>
        <thead>
          <tr>
            {headers.map((header, i) => (
              <th
                className={buildClassName(
                  alignments[i] === 'center' && styles.alignCenter,
                  alignments[i] === 'end' && styles.alignRight,
                )}
              >
                {renderCell(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr>
              {row.map((cell, i) => (
                <td
                  className={buildClassName(
                    alignments[i] === 'center' && styles.alignCenter,
                    alignments[i] === 'end' && styles.alignRight,
                  )}
                >
                  {renderCell(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default memo(MarkdownTable);
